import { useEffect, useRef, useState } from "react";

interface UseInlineRenameArgs {
  /** Canonical name/title from the server (or optimistic cache). */
  value: string;
  onCommit: (next: string) => void;
}

/**
 * Inline rename state shared by Ask Puntt list cards / sidebar-style editors.
 * Enter / blur saves; Escape cancels; unchanged / empty values are a no-op.
 * Guards so a single edit session commits at most once (blur after Escape
 * does not also save).
 */
export function useInlineRename({ value, onCommit }: UseInlineRenameArgs) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(value);
  const doneRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isRenaming) setDraft(value);
  }, [value, isRenaming]);

  function startRename() {
    doneRef.current = false;
    cancelledRef.current = false;
    setDraft(value);
    setIsRenaming(true);
  }

  function commit() {
    if (doneRef.current || cancelledRef.current) return;
    doneRef.current = true;
    const next = draft.trim();
    setIsRenaming(false);
    if (!next || next === value.trim()) {
      setDraft(value);
      return;
    }
    onCommit(next);
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(value);
    setIsRenaming(false);
  }

  return {
    isRenaming,
    draft,
    setDraft,
    startRename,
    commit,
    cancel,
  };
}
